import Link from "next/link"
import Image from "next/image"
import { Linkedin } from "@/components/icons/brand"
import { getContactDetails, ContactDetails } from "@/sanity/lib/fetch"

const groupBrands = [
  {
    name: "HPG Advisory Services",
    description: "Executive search and human capital advisory",
    href: "https://hpgadvisory.com",
  },
  {
    name: "Madison Mayfair",
    description: "Finding and attracting key players globally",
    href: "https://madisonmayfair.com",
  },
  {
    name: "FM Recruitment",
    description: "Specialists in hospitality financial recruitment",
    href: "https://fmrecruitment.co.uk",
  },
]

const footerLinks = [
  { name: "Privacy Policy", href: "/privacy" },
  { name: "Cookie Policy", href: "/cookies" },
  { name: "Modern Slavery Statement", href: "/modern-slavery" },
  { name: "Equal Opportunities", href: "/equal-opportunities" },
]

// Fallback contact details
const fallbackContact: ContactDetails = {
  addressLine1: "Thomas House",
  addressLine2: "84 Eccleston Square",
  city: "London",
  postcode: "SW1V 1PX",
  phone: "+44 (0) 20 8600 1166",
  phoneRaw: "+442086001166",
  email: "dan@hpgsearch.com",
  companyRegistration: "Registered in England No. 2163423",
}

export async function Footer() {
  let contact: ContactDetails | null = null
  
  try {
    contact = await getContactDetails()
  } catch {
    // Sanity not configured or fetch failed
  }
  
  // Use fallback if no contact details from Sanity
  if (!contact) {
    contact = fallbackContact
  }
  
  return (
    <footer className="bg-primary text-primary-foreground py-8">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & Company Info */}
          <div className="lg:col-span-1">
            <Image
              src="/logo.svg"
              alt="HPG Advisory Services"
              width={140}
              height={42}
              className="h-10 w-auto mb-4 brightness-0 invert"
            />
            {contact.companyRegistration && (
              <p className="text-primary-foreground/60 text-xs mb-1">
                {contact.companyRegistration}
              </p>
            )}
            <p className="text-primary-foreground/60 text-xs mb-4">
              VAT Number: 314604238
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-3 mt-6">
              <Link
                href="https://www.linkedin.com/company/hospitality-people-group/about/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-accent transition-colors"
                aria-label="Follow us on LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </Link>
            </div>
          </div>

          {/* Our Group */}
          <div className="lg:col-span-1">
            <h4 className="font-semibold mb-4 text-white">Our Group</h4>
            <ul className="space-y-3">
              {groupBrands.map((brand) => (
                <li key={brand.name}>
                  <Link
                    href={brand.href}
                    className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                    target={brand.href.startsWith("http") ? "_blank" : undefined}
                    rel={brand.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  >
                    {brand.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-1">
            <h4 className="font-semibold mb-4 text-white">Contact</h4>
            <address className="not-italic text-sm text-primary-foreground/70 space-y-2">
              {contact.addressLine1 && <p>{contact.addressLine1}</p>}
              {contact.addressLine2 && <p>{contact.addressLine2}</p>}
              {(contact.city || contact.postcode) && (
                <p>{[contact.city, contact.postcode].filter(Boolean).join(', ')}</p>
              )}
              {contact.phone && (
                <p className="pt-2">
                  <a
                    href={`tel:${contact.phoneRaw || contact.phone.replace(/\s+/g, '')}`}
                    className="hover:text-primary-foreground transition-colors"
                  >
                    {contact.phone}
                  </a>
                </p>
              )}
            </address>
          </div>

          {/* Legal */}
          <div className="lg:col-span-1">
            <h4 className="font-semibold mb-4 text-white">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    href={link.href}
                    className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-primary-foreground/10 mt-8 pt-6">
          <p className="text-center text-sm text-primary-foreground/60">
            © {new Date().getFullYear()} Hospitality People Group Ltd. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}
